import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  CalendarClock, ScrollText, Wrench, ArrowRight, LogOut,
  Target, CheckCircle2, Clock,
} from "lucide-react";
import { STAGES, stageChipClass, formatINR, formatDate } from "@/lib/constants";
import JourneyStepper from "@/components/JourneyStepper";
import BrandLogo from "@/components/BrandLogo";

const clientTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";

export default function PortalHome() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = sessionStorage.getItem("portal_token");
    if (!t) { navigate("/client-login", { replace: true }); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/portal/me`, { headers: { Authorization: `Bearer ${t}` } });
        setData(res.data);
      } catch (err) {
        sessionStorage.removeItem("portal_token");
        toast.error("Session expired — please request a new link");
        navigate("/client-login", { replace: true });
      } finally { setLoading(false); }
    })();
  }, [navigate]);

  const logout = () => {
    sessionStorage.removeItem("portal_token");
    sessionStorage.removeItem("portal_email");
    navigate("/client-login", { replace: true });
  };

  if (loading || !data) {
    return <div className="marketing hero-bg flex min-h-screen items-center justify-center text-muted-foreground">Loading your portal…</div>;
  }

  const { identity, appointments, plans, reviews, execution_tasks, goals } = data;
  const upcoming = appointments.filter((a) => ["Confirmed (Link Pending)", "Link Sent", "Rescheduled"].includes(a.status) && new Date(a.slot_datetime_utc) > new Date());
  const past = appointments.filter((a) => new Date(a.slot_datetime_utc) <= new Date() || ["Cancelled", "Completed", "No-show"].includes(a.status));
  const latestPlan = plans[0];
  const pendingReviews = reviews.filter((r) => !r.completed_at);

  return (
    <div className="marketing hero-bg min-h-screen">
      <header className="border-b border-black/5 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <BrandLogo size="sm" />
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden text-muted-foreground sm:inline" data-testid="portal-user-email">{identity.email}</span>
            <Button size="sm" variant="outline" onClick={logout} className="gap-1" data-testid="portal-logout-btn">
              <LogOut className="h-3 w-3" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10 md:py-12 space-y-8">
        <div>
          <p className="overline mb-1 text-muted-foreground">Your portal</p>
          <h1 className="font-dashboard text-3xl font-bold tracking-tight" data-testid="portal-greeting">
            Hi, {identity.full_name?.split(" ")[0] || "there"}.
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Everything happening between us, in one place.</p>
        </div>

        {/* Journey stage */}
        <Card className="border-border bg-white p-6" data-testid="portal-journey-card">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="overline text-muted-foreground">Where we are</div>
              <div className="mt-1"><span className={stageChipClass(identity.journey_stage)}>{identity.journey_stage}</span></div>
            </div>
            <div className="text-right text-xs text-muted-foreground">
              With us since<br /><b>{formatDate(identity.since)}</b>
            </div>
          </div>
          <JourneyStepper current={identity.journey_stage} testId="portal-stepper" />
        </Card>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Upcoming call */}
          <Card className="border-border bg-white p-6" data-testid="portal-upcoming-card">
            <div className="mb-4 flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-primary" />
              <div className="font-dashboard font-bold">Upcoming call</div>
            </div>
            {upcoming.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No upcoming calls scheduled. When we plan our next conversation, you'll see it here.
              </p>
            ) : upcoming.map((a) => (
              <div key={a.id} className="mb-3 last:mb-0 rounded-md border border-border bg-secondary/30 p-4" data-testid={`upcoming-appt-${a.id}`}>
                <div className="font-medium">
                  {new Date(a.slot_datetime_utc).toLocaleString("en-IN", {
                    weekday: "long", day: "2-digit", month: "long",
                    hour: "2-digit", minute: "2-digit", timeZone: clientTz,
                  })}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{clientTz} · {a.status}</div>
                {a.call_link && (
                  <a href={a.call_link} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground" data-testid={`join-call-${a.id}`}>
                    Join call <ArrowRight className="h-3 w-3" />
                  </a>
                )}
                <a href={`/manage-booking/${a.manage_token}`} className="mt-3 ml-2 text-xs text-muted-foreground hover:text-primary hover:underline" data-testid={`manage-appt-${a.id}`}>
                  Reschedule or cancel
                </a>
              </div>
            ))}
          </Card>

          {/* Latest plan */}
          <Card className="border-border bg-white p-6" data-testid="portal-plan-card">
            <div className="mb-4 flex items-center gap-2">
              <ScrollText className="h-4 w-4 text-primary" />
              <div className="font-dashboard font-bold">Your written plan</div>
            </div>
            {!latestPlan ? (
              <p className="text-sm text-muted-foreground">
                We'll draft your plan after Stage 2 (Goal Mapping). You'll see it here first — before anything is committed.
              </p>
            ) : (
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <span className="stage-chip stage-3 text-[10px]">v{latestPlan.version_number}</span>
                  <span className="text-xs text-muted-foreground">{latestPlan.status}</span>
                </div>
                <p className="mb-4 line-clamp-3 text-sm text-muted-foreground">
                  {latestPlan.investment_summary}
                </p>
                <a
                  href={`/plan-view/${latestPlan.share_token}`}
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium hover:bg-secondary"
                  data-testid="portal-open-plan"
                >
                  Open full plan <ArrowRight className="h-3 w-3" />
                </a>
              </div>
            )}
          </Card>
        </div>

        {/* Execution progress */}
        {execution_tasks.length > 0 && (
          <Card className="border-border bg-white p-6" data-testid="portal-execution-card">
            <div className="mb-4 flex items-center gap-2">
              <Wrench className="h-4 w-4 text-primary" />
              <div className="font-dashboard font-bold">Execution progress</div>
            </div>
            <div className="space-y-2">
              {execution_tasks.map((t) => (
                <div key={t.id} className="flex items-center justify-between rounded-md border border-border p-3 text-sm" data-testid={`portal-exec-${t.id}`}>
                  <div>
                    <div className="font-medium">{t.category}</div>
                    <div className="text-xs text-muted-foreground">{t.instrument} · {formatINR(t.target_amount)}</div>
                  </div>
                  <span className={
                    "rounded-full border px-2 py-0.5 text-xs " +
                    (t.status === "Confirmed" ? "border-primary/30 bg-primary/10 text-primary" :
                     t.status === "In Progress" ? "border-amber-300 bg-amber-50 text-amber-700" :
                     "border-border bg-white text-muted-foreground")
                  }>
                    {t.status === "Confirmed" && <CheckCircle2 className="mr-1 inline h-3 w-3" />}
                    {t.status}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Goals */}
        {goals.length > 0 && (
          <Card className="border-border bg-white p-6" data-testid="portal-goals-card">
            <div className="mb-4 flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              <div className="font-dashboard font-bold">Your goals</div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {goals.map((g) => (
                <div key={g.id} className="rounded-md border border-border p-3" data-testid={`portal-goal-${g.id}`}>
                  <div className="font-medium">{g.title}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {formatINR(g.target_amount)} · Target {formatDate(g.target_date)} · {g.priority}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Upcoming reviews */}
        {pendingReviews.length > 0 && (
          <Card className="border-border bg-white p-6" data-testid="portal-reviews-card">
            <div className="mb-4 flex items-center gap-2">
              <Clock className="h-4 w-4 text-accent" />
              <div className="font-dashboard font-bold">Upcoming reviews</div>
            </div>
            <div className="space-y-2">
              {pendingReviews.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-md border border-border p-3 text-sm">
                  <div>
                    <div className="font-medium">{r.trigger_type}{r.life_event_type ? ` · ${r.life_event_type}` : ""}</div>
                    <div className="text-xs text-muted-foreground">Due {formatDate(r.due_date)}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Past calls */}
        {past.length > 0 && (
          <details className="rounded-md border border-border bg-white p-4" data-testid="portal-past-calls">
            <summary className="cursor-pointer text-sm font-medium">Past calls ({past.length})</summary>
            <div className="mt-3 space-y-2 text-sm">
              {past.map((a) => (
                <div key={a.id} className="flex items-center justify-between border-t border-border py-2 first:border-0">
                  <span>{new Date(a.slot_datetime_utc).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: clientTz })}</span>
                  <span className="text-xs text-muted-foreground">{a.status}</span>
                </div>
              ))}
            </div>
          </details>
        )}

        <p className="pt-4 text-center text-xs text-muted-foreground">
          Need to talk to us directly? Reply to any of our emails or SMS — we're on it.
        </p>
      </main>
    </div>
  );
}
